import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { marked } from 'marked'
import { data } from 'react-router'
import { type Route } from './+types/chat-widget-privacy-policy.ts'

const MetaTitle = 'Chat Widget Privacy Notice | Dealer Pro Edge'
const MetaDescription =
	'Explains how personal information is collected, used, shared, retained, and protected through the Dealer Pro Edge chat widget.'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/chat-widget-privacy-policy'
	const canonicalUrl = `${origin}${path}`

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'robots', content: 'noindex, nofollow' },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
	]
}

// =============================================================================
// MARKDOWN CONTENT
// =============================================================================

const markdown = `# Dealer Pro Edge Chat Widget Privacy Notice

**Last Updated:** April 15, 2026

## 1. Introduction
This Dealer Pro Edge Chat Widget Privacy Notice ("Notice") explains how Dealer Pro Edge, LLC ("Dealer Pro Edge," "we," "us," or "our") collects, uses, discloses, stores, and protects personal information collected through the live chat and AI-assisted chat feature available on participating vehicle dealership websites (the "Chat Widget").

This Notice applies **only** to information collected through the Chat Widget and supplements our main Privacy Policy. The Chat Widget may not be enabled on every Dealer Pro Edge-powered site.

## 2. Information We Collect

### Information You Provide
- Name, email address, phone number
- Company or dealership information
- Content of your messages, appointment or inquiry details
- Files, images, or other materials you upload
- Any other information you voluntarily share

### Automatically Collected Information
- IP address, browser/device type and operating system
- Session identifiers, timestamps, and chat duration
- Pages viewed and referring URLs
- Approximate location (derived from IP address)

### AI Interaction Data
- Prompts, responses, and full conversation history
- Inferred intent, topics, routing decisions, and quality signals
- Data used for safety, spam detection, fraud prevention, and service improvement

We collect only personal information that is adequate, relevant, and reasonably necessary for the purposes described below (data minimization).

## 3. How We Use Information
We use this information to:
- Provide, operate, maintain, and improve the Chat Widget and its AI features
- Respond to your inquiries and route messages to the appropriate dealership
- Monitor, review, and enhance response quality and user experience
- Train, test, and improve our AI models and systems
- Support follow-up communications (including SMS where permitted)
- Detect fraud, abuse, or security issues
- Comply with legal obligations and enforce our policies

**AI Training Disclosure:** We may use personal data from Chat Widget conversations to train or improve large language models and other AI systems. We take reasonable steps to de-identify or minimize personal information where feasible for model development. This practice is disclosed here as required where applicable under privacy laws.

We may also create de-identified or aggregated data from chat interactions. We own such de-identified data and may use it for any lawful business purpose, including analytics, research, and further AI development.

## 4. How We Share Information
- **With the Dealership**: Your messages and contact details are shared with the specific dealership whose website you are visiting so they can respond to your inquiry.
- **With Service Providers**: We share data with trusted vendors that provide hosting, AI processing, analytics, and communications services. These providers are contractually obligated to protect your information and use it only for the purposes we specify.
- **Legal & Protection**: When required or permitted by law, to protect safety/rights, investigate fraud, or enforce terms.
- **Business Transfers**: In connection with a merger, acquisition, or sale of assets.

We do **not** sell personal information collected through the Chat Widget for third-party advertising purposes, nor do we use chat contact details to build advertising profiles.

## 5. Cookies and Tracking Technologies
The Chat Widget uses necessary cookies and session storage to maintain conversation continuity, security, and basic functionality. We minimize non-essential tracking.

## 6. Data Retention
We retain chat transcripts and related records only as long as reasonably necessary for customer service, quality assurance, legal compliance, dispute resolution, security, and service improvement. De-identified or aggregated data may be retained indefinitely for analytics and AI development. Full conversation history is not retained indefinitely where it is no longer needed.

## 7. Your Choices and Rights
You may request access to, correction, or deletion of your personal information collected through the Chat Widget by contacting us. To the extent required by applicable privacy laws, you may have additional rights (such as opting out of certain processing). We will respond to verified requests and provide an appeal process if a request is denied.

You can opt out of marketing or non-essential follow-up communications (see SMS section in our Chat Widget Terms of Use).

To exercise your rights, contact us below. We may verify your identity before processing requests.

## 8. Children's Privacy
The Chat Widget is not directed to children under 13 (or 16 where required by law). We do not knowingly collect personal information from children.

## 9. Security
We implement reasonable administrative, technical, and physical safeguards. However, no system is completely secure.

## 10. Changes to This Notice
We may update this Notice. Material changes will be noted with a new “Last Updated” date. Continued use of the Chat Widget after changes constitutes acceptance. Where required by law for material retroactive changes (e.g., new uses of data for AI training), we will provide additional notice and an opportunity to withdraw consent where applicable.

## 11. Contact Us
Dealer Pro Edge, LLC  
142 Swendsen Drive  
Monroe, CT 06468  
Email: privacy@dealerproedge.com  
`

// =============================================================================
// LOADER
// =============================================================================

export async function loader() {
	const html = await marked.parse(markdown)
	return data({ html })
}

// =============================================================================
// ROUTE COMPONENT
// =============================================================================

export default function ChatWidgetPrivacyNoticeRoute({
	loaderData,
}: Route.ComponentProps) {
	return (
		<main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
			<article
				className="prose prose-neutral max-w-none [&_h2]:mb-2 [&_h2]:mt-5"
				dangerouslySetInnerHTML={{ __html: loaderData.html }}
			/>
		</main>
	)
}
